import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Justify, Segment, Button } from 'tea-component';

import {
  FilterFixed,
  useFilterDataHook,
} from '@/pages/opinion/components/FilterCommon';
import TableCommon from '@/pages/opinion/components/TableCommon';

import { connect, useDispatch, useHistory, useIntl } from 'umi';

import '@/pages/opinion/assets/scss/pages/gamelist/index.scss';
import CardCommon from '../../components/CardCommon';
import {
  arrayToObj,
  arrayToSimpleObj,
  dealwithNewOldData,
  getPercentageValue,
  supplementArrayData,
  uniqueArray,
} from '../../utils';
import MentionsChart from './mentions-chart';
import TrendChart from './trend-chart';
import CardLoading from '../../components/CardLoading';
import useKeywordColumn from '../../hooks/useKeywordColumn';
import BubbleCommon, {
  gameListKeywordTips,
} from '../../components/BubbleCommon';
import { useInitBuriedPointInfo } from '../../operationLog/buriedPoint';
import GameListPoint from '@op/operationLog/pages/gameList';
import { excludeChannels } from '../../constants';
import RightSearchBox from './RightSearchBox';
import { genreSplitFlag } from '../../components/FilterCommon/constants/genreGame';
import { getKeyOpinionsRisingKeyword } from '@op/services/keyOpinions';
import { mergeExcludeChannel } from '@op/components/FilterCommon/utils';
import { useRequestFontWebConfig } from '@op/hooks/useFrontWebConfig';

const { Content } = Layout;

function GameList({
  userInfo,
  gameInfo,
  gameList,
  gameListLoading = true,
  gameStatsList,
  gameTimelineList,
  gameTimelineListLoading = true,
}: any) {
  const dispatch = useDispatch();
  const [mentionsList, setMentionsList] = useState<Array<any>>([]);
  const [localTrendList, setLocalTrendList] = useState<any>(gameTimelineList);
  const history = useHistory();
  useEffect(() => {
    setLocalTrendList(gameTimelineList);
  }, [gameTimelineList]);

  const { formatMessage } = useIntl();
  useInitBuriedPointInfo({ buriedPoint: GameListPoint });
  const { loading, filter, changeFilter } = useFilterDataHook({});
  const [searchValue, setSearchValue] = useState({
    keyword: '',
    edition_unified_id: '',
  });

  function dealwithGenre(value: any) {
    const { genre, sub_genre } = value || { genre: '', sub_genre: '' };
    const genreList = genre.split('|');
    const subGenreList = sub_genre.split('|');
    const genreTextList: Array<string> = [];
    genreList.forEach((key: string) => {
      const newKey = key === '' ? 'others' : key;
      subGenreList.forEach((subKey: string) => {
        const subNewKey = subKey === '' ? 'others' : subKey;
        genreTextList.push(
          `${newKey.toLocaleLowerCase()}${genreSplitFlag}${subNewKey.toLocaleLowerCase()}`,
        );
      });
    });

    return genreTextList;
  }

  const allGameMap = useMemo(() => {
    return arrayToObj(gameList, 'edition_unified_id');
  }, [gameList]);

  const genreGameListLocal = useMemo(() => {
    return gameList.map((item: any) => {
      return {
        ...item,
        genreList: dealwithGenre(item),
      };
    });
  }, [gameList]);

  useEffect(() => {
    if (!userInfo || !filter) return;
    dispatch({
      type: 'opinionGame/getGameStatsList',
      payload: mergeExcludeChannel(
        {
          sort_item: 'mentions',
          sort_model: 'desc',
          date_type: filter.date_type,
          end_time: filter.end_time,
          start_time: filter.start_time,
          metrics: ['mentions', 'sentiment', 'positive', 'neutral', 'negative'],
          edition_unified_ids: [],
        },
        excludeChannels,
      ),
    });
  }, [userInfo, gameInfo, filter]);

  useEffect(() => {
    if (!userInfo) return;
    setMentionsList([]);
    setLocalTrendList({});
    const { keyword, edition_unified_id } = searchValue;
    const params: any = {
      edition_unified_ids: [],
      entity_name: '',
      need_mobile_editions: true,
    };

    if (edition_unified_id) {
      params.edition_unified_ids = [edition_unified_id];
    } else if (keyword) {
      params.entity_name = keyword;
    }
    dispatch({
      type: 'opinionGame/getGameList',
      payload: params,
    });
  }, [userInfo, gameInfo, searchValue]);

  const maximumDuration: any = useMemo(() => {
    return {
      defaultNum: 90,
      defaultUnit: 'days',
      tips: formatMessage(
        {
          id: 'This page supports a maximum date range of 90 days. For longer-term game rankings, please visit {btn}.',
        },
        {
          btn: (
            <Button
              type="link"
              onClick={() => history.push('/opinion/Compare')}
            >
              {formatMessage({ id: 'the Compare page' })}
            </Button>
          ),
        },
      ),
    };
  }, []);

  const [tabType, setTabType] = useState('all');

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  useEffect(() => {
    setPageIndex(1);
    setPageSize(8);
  }, [filter]);

  const { loading: fontWebLoading, mobile_filter_high_game } =
    useRequestFontWebConfig({
      gameInfo: {
        edition_unified_id: 'xxxx',
        entity_type: 'mobile',
        id_type: 'unified_id',
      },
      needRequest: true,
      keys: [{ key: 'mobile_filter_high_game', defaultValue: [] }],
    });

  const realTableList = useMemo(() => {
    let tableList: any[] = [];
    if (gameStatsList) {
      const { new_list, old_list } = gameStatsList;

      const list = dealwithNewOldData<any>(
        new_list,
        old_list,
        'edition_unified_id',
        [
          'avg_sentiment',
          'mention',
          'negative_mention',
          'neutral_mention',
          'positive_mention',
        ],
      );
      const listMap = arrayToObj(list, 'edition_unified_id');

      tableList = genreGameListLocal?.map((item: any) => {
        const values = listMap[item.edition_unified_id] || {};
        return {
          mention: 0,
          mention_than: 0,
          avg_sentiment: 0,
          avg_sentiment_than: 0,
          ...item,
          ...values,
        };
      });

      if (tabType !== 'all')
        tableList = tableList.filter(
          (item: any) => item.source_type === tabType,
        );

      const { platform = [], game_list = [], genreGame } = filter || {};

      if (platform && Array.isArray(platform) && platform.length > 0) {
        tableList = tableList.filter((item: any) => {
          return platform.includes(item.entity_type);
        });
      }

      if (game_list && Array.isArray(game_list) && game_list.length > 0) {
        tableList = tableList.filter((item: any) => {
          return game_list.includes(item.edition_unified_id);
        });
      }

      // 过滤配置的游戏
      if(mobile_filter_high_game){
        tableList = tableList.filter((item: any) => {
          return !mobile_filter_high_game.includes(item.edition_unified_id);
        });
      }

      const genre = genreGame || [];
      if (genre.length !== 0) {
        tableList = tableList.filter((item: any) => {
          const genreList = item.genreList || [];
          // 判断一级
          return genreList.some((genreItem: string) => {
            return genre.includes(genreItem);
          });
        });
      }
    } 

    
    if (tableList.length === 0) {
      setMentionsList([]);
      setLocalTrendList({});
    }
    return tableList;
  }, [genreGameListLocal, gameStatsList, tabType, mobile_filter_high_game]);

  const trendList = useMemo(() => {
    if (localTrendList && filter) {
      const {
        timeline_of_end_time,
        timeline_of_start_time,
        mentions,
        sentiment,
      } = localTrendList || {};

      const [mentionsList, sentimentList] = supplementArrayData(
        timeline_of_start_time,
        timeline_of_end_time,
        [mentions, sentiment],
        filter.date_type,
      );

      const keyFun = ({ key, time }: any) => `${key}#_#${time}`;
      const mentionsKeys = mentionsList.map(keyFun);
      const sentimentKeys = sentimentList.map(keyFun);
      const allKeys = uniqueArray([...mentionsKeys, ...sentimentKeys]);
      const mentionsMap = arrayToSimpleObj(mentionsList, 'value', keyFun);
      const sentimentMap = arrayToSimpleObj(sentimentList, 'value', keyFun);
      const trendList = allKeys.map((key) => {
        const mentionItem = mentionsMap[key] || 0;
        const sentimentItem = sentimentMap[key] || 0;
        const [id, time] = key.split('#_#');
        return {
          mentions: mentionItem,
          name: id,
          sentiment: sentimentItem,
          time,
        };
      });
      return trendList;
    }
    return [];
  }, [localTrendList]);

  const { setRecord, KeywordColumn, keywordMap } = useKeywordColumn({
    dealwithKey: (param: any) => param.edition_unified_id,
    commonParams: filter,
    pageIndex,
    pageSize,
    getKeywordFunc: getKeyOpinionsRisingKeyword,
    dealwithResponse: (data) => data?.list || [],
    dealwithParams: (item, params) => {
      const { edition_unified_id, entity_type, id_type } = item || {};
      return {
        edition_unified_id,
        entity_type,
        id_type,
        top_n: 5,
        start_time: params.start_time,
        end_time: params.end_time,
        is_valid: [2, 1, 3],
        social_media_by_account: ['99999'],
        social_media_by_keyword: ['99999'],
        external_data_sources: ['99999'],
      };
    },
  });

  return (
    <Content className="dbo-page dbo-gamelist">
      <Content.Header
        title={
          <>
            {formatMessage({ id: 'Game List' })}
            {/* <span>
              {formatMessage({
                id: 'The mentions about the game is related to the monitoring configuration',
              })}
            </span> */}
          </>
        }
        subtitle={
          <div className="dbo-page-header-subtitle">
            {formatMessage({
              id: 'The mentions of the game are related to the monitoring configuration',
            })}
          </div>
        }
      />

      <Content.Body full>
        <FilterFixed
          filter={['date', 'platformGame']}
          operateShowOptions={null}
          isNoRequest
          isTile
          defaultConfig={{ date: { showTimeTips: false } }}
          confirm={(value) => {
            changeFilter(value);
          }}
          maximumDuration={maximumDuration}
        ></FilterFixed>
        <CardLoading loading={loading || fontWebLoading}>
          <Justify
            className="gamelist-justify"
            left={
              <>
                <Segment
                  className="segment-box segment-light"
                  value={tabType.toString()}
                  onChange={(value: any) => setTabType(value)}
                  options={[
                    {
                      text: (
                        <div className="dbo-select-flex">
                          {formatMessage({ id: 'All' })}
                        </div>
                      ),
                      value: 'all',
                    },
                    {
                      text: (
                        <div className="dbo-select-flex">
                          {formatMessage({ id: 'Tencent Games' })}
                        </div>
                      ),
                      value: 'tencentgames',
                    },
                    {
                      text: (
                        <div className="dbo-select-flex">
                          {formatMessage({ id: 'Competitors-1' })}
                        </div>
                      ),
                      value: 'competitors',
                    },
                  ]}
                />
              </>
            }
            right={
              <>
                {/* 展示屏蔽搜索 */}
                <RightSearchBox
                  className="edit-search-box"
                  sourceType={tabType}
                  onSearch={(name: string, id: string) => {
                    setSearchValue({ keyword: name, edition_unified_id: id });
                  }}
                  placeholder={'Search any game in the following table.'}
                />
              </>
            }
          />

          <CardCommon
            loading={gameListLoading}
            loadingHeight={300}
            emptyHeight={300}
            empty={!(realTableList?.length > 0)}
          >
            <TableCommon
              verticalTop={false}
              gameInfo={gameInfo}
              bordered={'all'}
              list={realTableList || []}
              total={realTableList?.length || 0}
              tableType="game-list"
              minWidth={1500}
              pageIndex={pageIndex}
              pageSize={pageSize}
              keywordMap={keywordMap}
              defaultColumns={{
                emerging_keyWords: {
                  key: 'emerging_keyWords',
                  width: 250,
                  header: (
                    <div className="dbo-table-header-flex">
                      <BubbleCommon
                        showIcon={false}
                        placement="top"
                        tipsMap={gameListKeywordTips}
                      >
                        {formatMessage({ id: 'Emerging Keywords' })}
                      </BubbleCommon>
                    </div>
                  ),
                  align: 'right',
                  render: (cvm: any) => {
                    return (
                      <KeywordColumn
                        active={false}
                        dataKey={cvm.edition_unified_id}
                      />
                    );
                  },
                },
              }}
              pageSizeOptions={[8, 10, 20, 30, 50, 100, 200]}
              sortType={'local'}
              onPagingChange={({ pageIndex, pageSize }: any) => {
                setPageIndex(pageIndex);
                setPageSize(pageSize);
              }}
              onRecordsChange={(records: any) => {
                const top8 = records.slice(0, 8);
                const mentionsList = top8.map((item: any) => {
                  const {
                    avg_sentiment = 0,
                    mention = 0,
                    negative_mention = 0,
                    neutral_mention = 0,
                    positive_mention = 0,
                    edition_unified_id,
                  } = item || ({} as any);
                  return {
                    entityName: allGameMap[edition_unified_id]?.entity_name,
                    id: edition_unified_id,
                    mentions: mention,
                    negativeNum: negative_mention,
                    negativeRate: getPercentageValue(
                      negative_mention,
                      mention,
                      6,
                    ),
                    neutralNum: neutral_mention,
                    neutralRate: getPercentageValue(
                      neutral_mention,
                      mention,
                      6,
                    ),
                    positiveNum: positive_mention,
                    positiveRate: getPercentageValue(
                      positive_mention,
                      mention,
                      6,
                    ),
                    sentiment: avg_sentiment,
                    sentimentNegative: 1,
                    sentimentNeutral: 3,
                    sentimentPositive: 5,
                  };
                });
                setMentionsList(mentionsList);
                if (mentionsList.length > 0) {
                  dispatch({
                    type: 'opinionGame/getGameListTimeline',
                    payload: {
                      date_type: filter.date_type,
                      start_time: filter.start_time,
                      end_time: filter.end_time,
                      edition_unified_ids: top8.map(
                        (item: { edition_unified_id: any }) =>
                          item.edition_unified_id,
                      ),
                    },
                  });
                }
                setRecord([...records]);
              }}
            ></TableCommon>
          </CardCommon>

          <CardCommon
            title={formatMessage({ id: 'Mentions & Sentiment' })}
            loading={gameListLoading}
            loadingHeight={300}
            emptyHeight={300}
            empty={!(mentionsList?.length > 0)}
          >
            <>
              <MentionsChart
                leftTitle={formatMessage({ id: 'Mentions' })}
                rightTitle={formatMessage({ id: 'Sentiment Score' })}
                mentionsList={mentionsList}
              ></MentionsChart>
            </>
          </CardCommon>

          <CardCommon
            title={formatMessage({ id: 'Trend' })}
            loading={gameTimelineListLoading}
            loadingHeight={300}
            emptyHeight={300}
            empty={!(trendList?.length > 0)}
          >
            <>
              <TrendChart
                sortList={
                  mentionsList?.map((item) => ({
                    id: item.id,
                    name: item.entityName,
                  })) || []
                }
                trendList={trendList}
                filter={filter}
              ></TrendChart>
            </>
          </CardCommon>
        </CardLoading>
      </Content.Body>
    </Content>
  );
}

export default connect((state: any) => {
  const { loading, home, opinionCommon, opinionFeedsFilter, opinionGame } =
    state;

  return {
    userInfoLoading: loading.effects['home/getUserInfo'],
    userInfo: home.userInfo,
    gameInfo: opinionCommon.gameInfo,

    gameList: opinionGame.gameList || [],
    gameListLoading: loading.effects['opinionGame/getGameList'],

    gameStatsList: opinionGame.gameStatsList,
    gameStatsListLoading: loading.effects['opinionGame/getGameStatsList'],

    gameTimelineList: opinionGame.gameTimelineList,
    gameTimelineListLoading: loading.effects['opinionGame/getGameListTimeline'],
  };
})(GameList);
